import type { LandingLocale } from '../../shared/content/landingContentTypes.js';
import type { StaticPageAudience } from '../../shared/content/staticPageContent.js';
import { setRoutingState } from '@adornis/router/client/routing-state-signal.js';
import { landingLocaleFromSearch, landingAudienceFromUrl } from '../../shared/landingDocumentUrl.js';
import { syncLandingDocumentMetadata } from './landingDocumentMetadata.js';
import { isPublicLandingPath } from '../../shared/landingPageSeo.js';

const localeStorageKey = 'vivaporta-landing-locale';

export class LandingLocaleState {
  static getInitialLocale(): LandingLocale {
    return typeof window === 'undefined' ? 'de' : landingLocaleFromSearch(new URLSearchParams(location.search));
  }

  static getAudience(): StaticPageAudience {
    return typeof window === 'undefined' ? 'applicant' : landingAudienceFromUrl(location.pathname, new URLSearchParams(location.search));
  }

  static persist(locale: LandingLocale, description = '') {
    if (typeof document !== 'undefined') syncLandingDocumentMetadata(locale, description);
    try { window.localStorage.setItem(localeStorageKey, locale); } catch { /* Preferences are optional. */ }
  }

  static change(locale: LandingLocale) {
    const search = new URLSearchParams(location.search);
    if (locale === 'de') search.delete('lang'); else search.set('lang', locale);
    void setRoutingState({ path: location.pathname, searchParams: search, hash: location.hash });
  }

  static changeAudience(audience: StaticPageAudience) {
    const search = new URLSearchParams(location.search);
    search.set('audience', audience);
    try { localStorage.setItem('vp-navigation-audience', audience); } catch { /* Preferences are optional. */ }
    void setRoutingState({ path: location.pathname, searchParams: search, hash: location.hash });
  }

  static subscribe(onLocale: (locale: LandingLocale) => void, onAudience: (audience: StaticPageAudience) => void) {
    const update = () => { onLocale(this.getInitialLocale()); onAudience(this.getAudience()); };
    window.addEventListener('location-changed', update);
    window.addEventListener('popstate', update);
    return () => {
      window.removeEventListener('location-changed', update);
      window.removeEventListener('popstate', update);
    };
  }

  /** Restore a preference once, expressing it in the URL before the router mounts. */
  static restorePreferences() {
    if (!isPublicLandingPath(location.pathname)) return;
    const url = new URL(location.href);
    try {
      const locale = localStorage.getItem(localeStorageKey);
      if (!url.searchParams.has('lang') && (locale === 'en' || locale === 'tr')) url.searchParams.set('lang', locale);
      if (url.pathname === '/' && !url.searchParams.has('audience') && localStorage.getItem('vp-navigation-audience') === 'company')
        url.searchParams.set('audience', 'company');
    } catch { /* A clean browser uses the server's DE/default audience. */ }
    if (url.href !== location.href) {
      history.replaceState(history.state, '', url);
      window.dispatchEvent(new CustomEvent('location-changed'));
    }
  }
}
